import { Injectable, TemplateRef, EventEmitter } from '@angular/core';
import { Observable, of } from 'rxjs';

@Injectable()
export class ModalOptions {
    /**
     *  Includes a modal-backdrop element. Alternatively,
     *  specify static for a backdrop which doesn't close the modal on click.
     */
    backdrop?: boolean | 'static';
    /**
     * Closes the modal when escape key is pressed.
     */
    keyboard?: boolean;

    focus?: boolean;
    /**
     * Shows the modal when initialized.
     */
    show?: boolean;
    /**
     * Ignore the backdrop click
     */
    ignoreBackdropClick?: boolean;
    /**
     * Css class for opened modal
     */
    class?: string;
    /**
     * Toggle animation
     */
    animated?: boolean;
    /**
     * Modal data
     */
    initialState?: object;
    /**
     * 窗口标题
     */
    title: string;
    /** 标题前图标 */
    iconCls?: string;

    /** 显示头部 */
    showHeader?: boolean;

    /**
     * 显示关闭按钮
     */
    showCloseButton?: boolean;
    /**
     * 显示最大化按钮
     */
    showMaxButton?: boolean;
    /**
     * 显示最小化按钮
     */
    showMinButton?: boolean;

    /**
     * 窗口宽度
     */
    width?: number;
    /**
     * 窗口高度
     */
    height?: number;

    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    /** 窗口标题栏高度 */
    dialogHeaderHeight?: number;
    /** 窗口按钮区高度 */
    dialogFooterHeight?: number;

    dialogFooterStyles?: any;

    /**
     * 显示按钮区
     */
    showButtons?: boolean;
    /** 按钮对齐方式 */
    buttonAlign?: string;

    /** 按钮列表模板 */
    buttons?: TemplateRef<any> | DialogButton[];

    /** 启用拖拽功能 */
    draggable?: boolean;

    /** 启用调整尺寸大小 */
    resizable?: boolean;

    /** 是否启用滚动条 */
    enableScroll?: boolean;

    beforeOpen?: () => Observable<boolean>;
    beforeClose?: (modalRef: any, args?: any) => Observable<boolean>;
    opened?: ( p?: {[key: string]: any }) => void;
    closed?: (isCloseButtonClick: boolean | any, args?: any) => void;
    okText?: string;
    cancelText?: string;
    /** 弹窗类型：default，弹出组件、模板等；iframe,弹出iframe; ngFactory, 弹出ngFactory; */
    dialogType?: string;
    // 窗口适应内容自动
    fitContent?: boolean;
    // 窗口内容区域是否自动响应式
    areaResponse?: boolean;
    // 异常提示
    exception?: {
        date: string,
        message: string
    };
   // 是否非模态
   modeless?:boolean;
   // 默认是缩略图类型
   defaultSnapType?:string;
   // 是否启用缩略图
   canSnap?:boolean;
}

export interface DialogButton {
    text: string;
    cls?: string;
    iconCls?: string;
    disable?: boolean;
    defaultFocus?: boolean;
    handle?: (e) => any;
    display?: boolean;
    tipsEnable?: boolean;
    tipsText?: string | ((e?: any) => string);
}

export const modalConfigDefaults: ModalOptions = {
    backdrop: 'static',
    keyboard: true,
    focus: true,
    show: false,
    ignoreBackdropClick: true,
    class: '',
    animated: true,
    initialState: {},
    width: 400,
    height: 350,
    minHeight: null,
    minWidth: null,
    maxHeight: null,
    maxWidth: null,
    title: 'Title is here.',
    showCloseButton: true,
    showMaxButton: true,
    showButtons: true,
    buttonAlign: 'center',
    draggable: true,
    resizable: true,
    enableScroll: true,
    beforeOpen: () => of(true),
    beforeClose: () => of(true),
    okText: '确定',
    cancelText: '取消',
    dialogHeaderHeight: 38,
    dialogFooterHeight: 50,
    dialogType: 'default',
    showHeader: true,
    fitContent: false,
    areaResponse: false,
    canSnap:false
};

export const CLASS_NAME: any = {
    SCROLLBAR_MEASURER: 'modal-scrollbar-measure',
    BACKDROP: 'modal-backdrop',
    OPEN: 'modal-open',
    FADE: 'fade',
    IN: 'in', // bs3
    SHOW: 'show' // bs4
};

export const SELECTOR: any = {
    DIALOG: '.modal-dialog',
    DATA_TOGGLE: '[data-toggle="modal"]',
    DATA_DISMISS: '[data-dismiss="modal"]',
    FIXED_CONTENT: '.navbar-fixed-top, .navbar-fixed-bottom, .is-fixed'
};

export const TRANSITION_DURATIONS: any = {
    MODAL: 300,
    BACKDROP: 150
};

export const DISMISS_REASONS = {
    BACKRDOP: 'backdrop-click',
    ESC: 'esc'
};
export interface SNPChildPos{
    top?:number|string;
    bottom?:number|string;
    left?:number|string;
    right?:number|string;
    width?:number|string;
    height?:number|string;
}
export interface SNAPChild{
    type:string;
    value:SNPChildPos;
    gap?:SNPChildPos
}
export interface SNAP{
    type:string;
    value:SNPChildPos;
    children: Array<SNAPChild>;
}
/**
 * width:'0.5|2',表示 宽度是参照宽度0.5，减去两个间距
 * 计算逻辑，先按比例分，再减去间隔
 */
const SNAP_TYPE:Array<SNAP> = [
    {
        type: "two-equal",
        value: { height: '1|2', width: 0.5 },
        children: [
            {
                type: "two-equal_left",
                value: { left: 0, top: 0, bottom: -1, right: -1 }
            },
            {
                type: "two-equal_right",
                value: { width:'0.5|2',left: -1, top: 0, bottom: -1, right: 0 }
            }
        ]
    },
    {
        type: "two-main",
        value: { height: '1|2', width: -1 },
        children: [
            {
                type: "two-main_main",
                value: { width: 0.66, left: 0, top: 0, bottom: -1, right: -1 }
            },
            {
                type: "two-main_right",
                value: { width: '0.34|2', left: -1, top: 0, bottom: -1, right: 0 }
            }
        ]
    },
    {
        type: "three-equal",
        value: { height: '1|2', width: 0.325 },
        children: [
            {
                type: "three-equal_left",
                value: { left: 0, top: 0, bottom: -1, right: -1 }
            },
            {
                type: "three-equal_center",
                value: { width: '0.35|2', left: '0.325|1', top: 0, bottom: -1, right: -1 }
            },
            {
                type: "three-equal_right",
                value: { left: -1, top: 0, bottom: -1, right: 0 }
            }
        ]
    },
    {
        type: "three-mixed",
        value: { height: '1|2', width: '0.5|1.5' },
        children: [
            {
                type: "three-mixed_left",
                value: { left: 0, top: 0, bottom: -1, right: -1 }
            },
            {
                type: "three-mixed_top-right",
                value: { height: '0.5|1.5', left: -1, top: 0, bottom: -1, right: 0 }
            },
            {
                type: "three-mixed_bottom-right",
                value: { height: '0.5|1.5', left: -1, top: -1, bottom: 0, right: 0 }
            }
        ]
    },
    {
        type: "four-equal",
        value: { height: '0.5|1.5', width: '0.5|1.5' },
        children: [
            {
                type: "four-equal_top-left",
                value: { left: 0, top: 0, bottom: -1, right: -1 }
            },
            {
                type: "four-equal_top-right",
                value: { left: -1, top: 0, bottom: -1, right: 0 }
            },
            {
                type: "four-equal_bottom-left",
                value: { left: 0, top: -1, bottom: 0, right: -1 }
            },
            {
                type: "four-equal_bottom-right",
                value: { left: -1, top: -1, bottom: 0, right: 0 }
            }
        ]
    },
    {
        type: "three-main",
        value: { height: "1|2", width: 0.25 },
        children: [{
            type: "three-main_left",
            value: { left: 0, top: 0, bottom: -1, right: -1 }
        },
        {
            type: "three-main_main",
            value: { width: '0.5|2', left: 0.25, top: 0, bottom: 0, right: -1 },
        },
        {
            type: "three-main_right",
            value: { left: -1, top: 0, bottom: -1, right: 0 }
        }]
    }
];
export enum SnapSize {
    large = 'large',
    middle='middle',
    small = 'small',
}

export enum SnapSelectMode{
    single='single',
    whole='whole'
}
/**
 * 
 */
export interface SnapClickEventInfo{
    selectMode:SnapSelectMode,//单个选择还是整体选择
    snapType:string,
    snapInfo:SNPChildPos| Array<SNAPChild>|null// 单个选择返回信息，整体选择返回数组
}

const getSnapTypes=function(){
    return SNAP_TYPE;
}
/**
 * 单个返回位置，用于计算
 * 整体返回元素，用于调用者获取信息
 * @param selectMode 选择模式:整体选择还是单个选择
 * @param snapTypeStr 位置类型字符串：
 */
const findSnapType=function(snaps,selectMode,snapTypeStr){
    snaps=snaps?snaps:getSnapTypes();
    var curSnaps=snapTypeStr.split('_');
    let wholeSnap=SNAP_TYPE.find(item=>item.type==curSnaps[0]);
    wholeSnap=wholeSnap?wholeSnap:SNAP_TYPE[0];
    if(curSnaps.length==2||selectMode==SnapSelectMode.single){
        // 单个
        let curSnap=(wholeSnap['children'] as Array<any>).find(item=>item.type==snapTypeStr);
      
        if(curSnap){
            return Object.assign({},wholeSnap.value,curSnap.value);
        }
        return {width:1,height:1,left:0,top:0,right:-1,bottom:-1};
    }else{
        // 整个        
        return wholeSnap.children;
    }
}
/**
 * 根据指定的宽、高、间距 计算位置
 * @param baseWidth 
 * @param baseHeight 
 * @param gap 
 */
const getCalculateSize=function(baseWidth,baseHeight,gap,positionInfo){
    baseWidth=baseWidth>0?baseWidth:window.innerWidth;
    baseHeight=baseHeight>0?baseHeight:window.innerHeight;
    /**计算 */
    let result={width:1,height:1,left:-1,right:-1,top:-1,bottom:-1};
    result=Object.assign(result,positionInfo);
    for(var key in result){
        let baseCalSize=['width','left','right'].indexOf(key)>-1?baseWidth:baseHeight;
        result[key]=calculateSize(key,result[key],baseCalSize,gap) as any;
    }
    return result;
}
function calculateSize(propKey,propVal,baseSize,gap){
    let calPlus=propKey=='width'||propKey=='height'?-1:1
    if(typeof propVal=='string'){
        let callist=propVal.split('|');
        if(callist.length>1){
            return parseFloat(callist[0])*baseSize+parseFloat(callist[1])*calPlus*gap;
        }
        return parseFloat(callist[0])*baseSize+calPlus*gap;
    }
    return propVal<0?'auto':propVal*baseSize+calPlus*gap;
}

export const SnapUtils={
    getSnapTypes,findSnapType,getCalculateSize
}